/**
 * DGA utility row under the stamp: weather, local date/time, location (from GPS + APIs when allowed),
 * and a toolbar for root font zoom + grayscale contrast (shared with the footer).
 */
import { useCallback, useEffect, useState } from "react";
import type { ReactNode } from "react";
import {
  DGA_GRAYSCALE_CHANGE_EVENT,
  getGrayscaleModeEnabled,
  toggleGrayscaleMode,
} from "lib/dgaGrayscaleMode";
import { sanitizeDisplayText, sanitizeLocale } from "lib/safeDisplayText";
import { getSecondNavMessages, weatherCodeToLabel } from "components/Feature/Navigation/HeaderNavigations/DGASecondNav/headerSecondNavI18n";
import { useHeaderSecondNavGeo } from "components/Feature/Navigation/HeaderNavigations/DGASecondNav/useHeaderSecondNavGeo";


const ICON_FILL = "#384250";
/** Figma action toolbar glyphs (voice / zoom / contrast, #161616). */
const ACTION_TOOLBAR_ICON_FILL = "#161616";

/** Matches Figma `Second Nav Header` / Actions: 32×32 control, 20×20 asset, `radius-sm`, no extra wrappers. */
const ACTION_BTN_CLASS =
  "flex h-8 w-8 min-h-8 min-w-8 max-h-8 max-w-8 shrink-0 items-center justify-center overflow-hidden rounded p-0 transition-colors hover:bg-neutral-200/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:pointer-events-none disabled:opacity-40";

// --- Root font zoom (html { font-size: N% }, persisted in sessionStorage) ---

const ZOOM_STORAGE_KEY = "dga-root-font-zoom-pct";
const DEFAULT_ZOOM_PCT = 100;
const MIN_ZOOM_PCT = 80;
const MAX_ZOOM_PCT = 120;
const ZOOM_STEP_PCT = 5;

function applyRootFontZoom(pct: number): number {
  const clamped = Math.min(MAX_ZOOM_PCT, Math.max(MIN_ZOOM_PCT, pct));
  document.documentElement.style.fontSize = `${clamped}%`;
  try {
    sessionStorage.setItem(ZOOM_STORAGE_KEY, String(clamped));
  } catch {
    /* quota / private mode */
  }
  return clamped;
}

function readStoredZoom(): number {
  if (typeof window === "undefined") return DEFAULT_ZOOM_PCT;
  try {
    const s = sessionStorage.getItem(ZOOM_STORAGE_KEY);
    if (s) {
      const n = parseInt(s, 10);
      if (!Number.isNaN(n)) return Math.min(MAX_ZOOM_PCT, Math.max(MIN_ZOOM_PCT, n));
    }
  } catch {
    /* access denied */
  }
  return DEFAULT_ZOOM_PCT;
}

// --- Date/time strings (no commas; safe for screen readers) ---

/** Removes locale commas (e.g. US "Sep 3, 2026"); uses hyphens between parts for LTR locales. */
function formatNavDate(d: Date, locale: string): string {
  const loc = sanitizeLocale(locale);
  const raw = d.toLocaleDateString(loc, { day: "numeric", month: "short", year: "numeric" });
  const noComma = raw.replace(/,/g, "").trim();
  const isArabic = loc.toLowerCase().startsWith("ar");
  const normalized = isArabic ? noComma.replace(/\s+/g, " ") : noComma.replace(/\s+/g, "-");
  return sanitizeDisplayText(normalized, 64);
}

function formatNavTime(d: Date, locale: string): string {
  const loc = sanitizeLocale(locale);
  const raw = d.toLocaleTimeString(loc, { hour: "numeric", minute: "2-digit" });
  return sanitizeDisplayText(raw.replace(/,/g, "").trim(), 32);
}

// --- Small presentational pieces (icons, row item, toolbar) ---

/** Same box + SVG scale as context icons (20×20 glyph in 24×24 cell). */
const NAV_ICON_CELL =
  "relative flex h-6 w-6 shrink-0 items-center justify-center [&_svg]:h-5 [&_svg]:w-5";

type SecondNavItemProps = {
  icon: ReactNode;
  visibleLabel: string;
  groupAriaLabel: string;
  /** Replaces the text with a small spinner (geolocation / API loading) */
  isLoading?: boolean;
};

const SecondNavInlineSpinner = (): JSX.Element => (
  <span
    className="inline-block h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-neutral-400 border-t-primary-600"
    role="status"
    aria-hidden="true"
  />
);

const SecondNavItem = ({
  icon,
  visibleLabel,
  groupAriaLabel,
  isLoading,
}: SecondNavItemProps): JSX.Element => (
  <li className="list-none">
    <div
      className="flex min-w-0 items-center gap-1"
      role="group"
      aria-label={groupAriaLabel}
      aria-busy={isLoading ? true : undefined}
    >
      <span className={NAV_ICON_CELL} aria-hidden="true">
        {icon}
      </span>
      <span className="flex min-h-6 min-w-[1.25rem] items-center whitespace-nowrap text-base font-normal leading-6 text-neutral-700">
        {isLoading ? <SecondNavInlineSpinner /> : visibleLabel}
      </span>
    </div>
  </li>
);

const IconCloud = (): JSX.Element => (
  <svg viewBox="0 0 24 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M7.14 3.39C6.52 4.43 6.5 5.72 6.5 6.75C6.5 7.16 6.16 7.5 5.75 7.5C5.04 7.5 3.95 7.72 3.06 8.36C2.21 8.97 1.5 9.99 1.5 11.75C1.5 13.51 2.21 14.53 3.06 15.14C3.95 15.78 5.04 16 5.75 16H17.75C18.46 16 19.55 15.78 20.44 15.14C21.29 14.53 22 13.51 22 11.75C22 9.99 21.29 8.97 20.44 8.36C19.55 7.72 18.46 7.5 17.75 7.5C17.34 7.5 17 7.16 17 6.75C17 5.72 16.98 4.43 16.36 3.39C15.78 2.42 14.57 1.5 11.75 1.5C8.93 1.5 7.72 2.42 7.14 3.39ZM5.86 2.61C6.78 1.08 8.57 0 11.75 0C14.93 0 16.72 1.08 17.64 2.61C18.33 3.76 18.47 5.07 18.49 6.05C19.36 6.16 20.4 6.48 21.31 7.14C22.55 8.03 23.5 9.51 23.5 11.75C23.5 13.99 22.55 15.47 21.31 16.36C20.12 17.22 18.7 17.5 17.75 17.5H5.75C4.8 17.5 3.38 17.22 2.19 16.36C0.95 15.47 0 13.99 0 11.75C0 9.51 0.95 8.03 2.19 7.14C3.1 6.48 4.14 6.16 5.01 6.05C5.03 5.07 5.17 3.76 5.86 2.61Z"
      fill={ICON_FILL}
    />
  </svg>
);

/** Calendar icon only (no English wordmark — real date is shown beside the icon). */
const IconCalendar = (): JSX.Element => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M7 1.75C7.41421 1.75 7.75 2.08579 7.75 2.5V6.5C7.75 6.91421 7.41421 7.25 7 7.25C6.58579 7.25 6.25 6.91421 6.25 6.5V5.25H5C4.30964 5.25 3.75 5.80964 3.75 6.5V9.75H20.25V6.5C20.25 5.80964 19.6904 5.25 19 5.25H18.5C18.0858 5.25 17.75 4.91421 17.75 4.5C17.75 4.08579 18.0858 3.75 18.5 3.75H19C20.5188 3.75 21.75 4.98122 21.75 6.5V19.5C21.75 21.0188 20.5188 22.25 19 22.25H5C3.48122 22.25 2.25 21.0188 2.25 19.5V6.5C2.25 4.98122 3.48122 3.75 5 3.75H6.25V2.5C6.25 2.08579 6.58579 1.75 7 1.75ZM3.75 19.5C3.75 20.1904 4.30964 20.75 5 20.75H19C19.6904 20.75 20.25 20.1904 20.25 19.5V11.25H3.75V19.5Z"
      fill={ICON_FILL}
    />
    <path
      d="M15 1.75C15.4142 1.75 15.75 2.08579 15.75 2.5V6.5C15.75 6.91421 15.4142 7.25 15 7.25C14.5858 7.25 14.25 6.91421 14.25 6.5V5.25H10.5C10.0858 5.25 9.75 4.91421 9.75 4.5C9.75 4.08579 10.0858 3.75 10.5 3.75H14.25V2.5C14.25 2.08579 14.5858 1.75 15 1.75Z"
      fill={ICON_FILL}
    />
  </svg>
);

const IconClock = (): JSX.Element => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
    <path
      d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
      stroke={ICON_FILL}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 6V12H16.5"
      stroke={ICON_FILL}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Zoom out (Figma SVG, 20×20 in 32×32 control). */
const IconZoomOut = (): JSX.Element => (
  <svg
    width={20}
    height={20}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className="pointer-events-none block shrink-0 select-none"
  >
    <path
      d="M1.63416 12.7038C1.97582 12.6871 2.27582 12.9538 2.29249 13.2954C2.38416 15.1121 2.65082 16.0454 3.29249 16.6788C4.02582 17.4121 5.14249 17.6454 7.50082 17.7038C7.84249 17.7038 8.11749 17.9954 8.10915 18.3454C8.10915 18.6871 7.81749 18.9621 7.46749 18.9538C5.15082 18.9038 3.53416 18.6954 2.40082 17.5621C1.40916 16.5704 1.13416 15.2121 1.04249 13.3621C1.02582 13.0204 1.29249 12.7204 1.63416 12.7038Z"
      fill={ACTION_TOOLBAR_ICON_FILL}
    />
    <path
      d="M18.3675 12.7038C18.7092 12.7204 18.9758 13.0121 18.9592 13.3621C18.8675 15.2121 18.5925 16.5788 17.5925 17.5704C16.4592 18.6954 14.8425 18.9038 12.5258 18.9621C12.1842 18.9621 11.8925 18.6954 11.8842 18.3538C11.8842 18.0121 12.1508 17.7204 12.4925 17.7121C14.8508 17.6621 15.9675 17.4204 16.7008 16.6871C17.3425 16.0538 17.6092 15.1204 17.7008 13.3038C17.7175 12.9621 18.0092 12.6954 18.3592 12.7121L18.3675 12.7038Z"
      fill={ACTION_TOOLBAR_ICON_FILL}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.58416 5.20378C12.0008 5.20378 13.9592 7.16211 13.9592 9.57878C13.9592 10.5621 13.6342 11.4704 13.0842 12.1954L14.6092 13.7204C14.8508 13.9621 14.8508 14.3621 14.6092 14.6038C14.3675 14.8454 13.9675 14.8454 13.7258 14.6038L12.2008 13.0788C11.4675 13.6288 10.5592 13.9454 9.58416 13.9454C7.16749 13.9454 5.20916 11.9871 5.20916 9.57044C5.20916 7.15378 7.16749 5.19544 9.58416 5.19544V5.20378ZM9.58416 6.45378C7.85916 6.45378 6.45916 7.85378 6.45916 9.57878C6.45916 11.3038 7.85916 12.7038 9.58416 12.7038C11.3092 12.7038 12.7092 11.3038 12.7092 9.57878C12.7092 7.85378 11.3092 6.45378 9.58416 6.45378Z"
      fill={ACTION_TOOLBAR_ICON_FILL}
    />
    <path
      d="M7.47582 1.03711C7.81749 1.03711 8.10915 1.30378 8.11749 1.64544C8.11749 1.98711 7.85082 2.27878 7.50916 2.28711C5.15082 2.33711 4.03416 2.57878 3.30082 3.31211C2.65916 3.94544 2.39249 4.87878 2.30082 6.69544C2.28416 7.03711 1.99249 7.30378 1.64249 7.28711C1.30082 7.27044 1.03416 6.97878 1.05082 6.62878C1.13416 4.77878 1.40916 3.42044 2.40916 2.42878C3.54249 1.29544 5.15915 1.08711 7.47582 1.03711Z"
      fill={ACTION_TOOLBAR_ICON_FILL}
    />
    <path
      d="M12.5258 1.03711C14.8425 1.08711 16.4592 1.29544 17.5925 2.42878C18.5925 3.42044 18.8675 4.77878 18.9592 6.63711C18.9758 6.97878 18.7092 7.27878 18.3675 7.29544C18.0258 7.31211 17.7258 7.04544 17.7092 6.70378C17.6175 4.88711 17.3508 3.95378 16.7092 3.32044C15.9758 2.58711 14.8592 2.35378 12.5008 2.29544C12.1592 2.29544 11.8842 2.00378 11.8925 1.65378C11.8925 1.31211 12.1842 1.03711 12.5342 1.04544L12.5258 1.03711Z"
      fill={ACTION_TOOLBAR_ICON_FILL}
    />
    <path
      d="M7.82582 8.95378C7.48416 8.95378 7.20082 9.23711 7.20082 9.57878C7.20082 9.92044 7.48416 10.2038 7.82582 10.2038H11.0758C11.4175 10.2038 11.7008 9.92044 11.7008 9.57878C11.7008 9.23711 11.4175 8.95378 11.0758 8.95378H7.82582Z"
      fill={ACTION_TOOLBAR_ICON_FILL}
    />
  </svg>
);

/** Zoom in (Figma SVG, 20×20 in 32×32 control). */
const IconZoomIn = (): JSX.Element => (
  <svg
    width={20}
    height={20}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className="pointer-events-none block shrink-0 select-none"
  >
    <path
      d="M1.63416 12.7038C1.97582 12.6871 2.27582 12.9538 2.29249 13.2954C2.38416 15.1121 2.65082 16.0454 3.29249 16.6788C4.02582 17.4121 5.14249 17.6454 7.50082 17.7038C7.84249 17.7038 8.11749 17.9954 8.10915 18.3454C8.10915 18.6871 7.81749 18.9621 7.46749 18.9538C5.15082 18.9038 3.53416 18.6954 2.40082 17.5621C1.40916 16.5704 1.13416 15.2121 1.04249 13.3621C1.02582 13.0204 1.29249 12.7204 1.63416 12.7038Z"
      fill={ACTION_TOOLBAR_ICON_FILL}
    />
    <path
      d="M18.3675 12.7038C18.7092 12.7204 18.9758 13.0121 18.9592 13.3621C18.8675 15.2121 18.5925 16.5788 17.5925 17.5704C16.4592 18.6954 14.8425 18.9038 12.5258 18.9621C12.1842 18.9621 11.8925 18.6954 11.8842 18.3538C11.8842 18.0121 12.1508 17.7204 12.4925 17.7121C14.8508 17.6621 15.9675 17.4204 16.7008 16.6871C17.3425 16.0538 17.6092 15.1204 17.7008 13.3038C17.7175 12.9621 18.0092 12.6954 18.3592 12.7121L18.3675 12.7038Z"
      fill={ACTION_TOOLBAR_ICON_FILL}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.58416 5.20378C12.0008 5.20378 13.9592 7.16211 13.9592 9.57878C13.9592 10.5621 13.6342 11.4704 13.0842 12.1954L14.6092 13.7204C14.8508 13.9621 14.8508 14.3621 14.6092 14.6038C14.3675 14.8454 13.9675 14.8454 13.7258 14.6038L12.2008 13.0788C11.4675 13.6288 10.5592 13.9454 9.58416 13.9454C7.16749 13.9454 5.20916 11.9871 5.20916 9.57044C5.20916 7.15378 7.16749 5.19544 9.58416 5.19544V5.20378ZM9.58416 6.45378C7.85916 6.45378 6.45916 7.85378 6.45916 9.57878C6.45916 11.3038 7.85916 12.7038 9.58416 12.7038C11.3092 12.7038 12.7092 11.3038 12.7092 9.57878C12.7092 7.85378 11.3092 6.45378 9.58416 6.45378Z"
      fill={ACTION_TOOLBAR_ICON_FILL}
    />
    <path
      d="M7.47582 1.03711C7.81749 1.03711 8.10915 1.30378 8.11749 1.64544C8.11749 1.98711 7.85082 2.27878 7.50916 2.28711C5.15082 2.33711 4.03416 2.57878 3.30082 3.31211C2.65916 3.94544 2.39249 4.87878 2.30082 6.69544C2.28416 7.03711 1.99249 7.30378 1.64249 7.28711C1.30082 7.27044 1.03416 6.97878 1.05082 6.62878C1.13416 4.77878 1.40916 3.42044 2.40916 2.42878C3.54249 1.29544 5.15915 1.08711 7.47582 1.03711Z"
      fill={ACTION_TOOLBAR_ICON_FILL}
    />
    <path
      d="M12.5258 1.03711C14.8425 1.08711 16.4592 1.29544 17.5925 2.42878C18.5925 3.42044 18.8675 4.77878 18.9592 6.63711C18.9758 6.97878 18.7092 7.27878 18.3675 7.29544C18.0258 7.31211 17.7258 7.04544 17.7092 6.70378C17.6175 4.88711 17.3508 3.95378 16.7092 3.32044C15.9758 2.58711 14.8592 2.35378 12.5008 2.29544C12.1592 2.29544 11.8842 2.00378 11.8925 1.65378C11.8925 1.31211 12.1842 1.03711 12.5342 1.04544L12.5258 1.03711Z"
      fill={ACTION_TOOLBAR_ICON_FILL}
    />
    <path
      d="M9.58416 7.47878C9.25916 7.47878 9.00082 7.73711 9.00082 8.06211V8.99544H8.06749C7.74249 8.99544 7.48416 9.25378 7.48416 9.57878C7.48416 9.90378 7.74249 10.1621 8.06749 10.1621H9.00082V11.0954C9.00082 11.4204 9.25916 11.6788 9.58416 11.6788C9.90915 11.6788 10.1675 11.4204 10.1675 11.0954V10.1621H11.1008C11.4258 10.1621 11.6842 9.90378 11.6842 9.57878C11.6842 9.25378 11.4258 8.99544 11.1008 8.99544H10.1675V8.06211C10.1675 7.73711 9.90915 7.47878 9.58416 7.47878Z"
      fill={ACTION_TOOLBAR_ICON_FILL}
    />
  </svg>
);

/** Contrast / view (Figma SVG, eye — 20×20 in 32×32 control). */
const IconContrastView = (): JSX.Element => (
  <svg
    width={20}
    height={20}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className="pointer-events-none block shrink-0 select-none"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M16.9302 11.0895C14.1513 4.91418 5.84899 4.91418 3.07011 11.0895C2.92846 11.4043 2.55845 11.5446 2.24368 11.403C1.9289 11.2613 1.78855 10.8913 1.9302 10.5765C5.15132 3.4185 14.849 3.4185 18.0701 10.5765C18.2118 10.8913 18.0714 11.2613 17.7566 11.403C17.4419 11.5446 17.0719 11.4043 16.9302 11.0895Z"
      fill={ACTION_TOOLBAR_ICON_FILL}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10.0002 9.79134C11.0357 9.79134 11.8752 10.6308 11.8752 11.6663C11.8752 12.7019 11.0357 13.5413 10.0002 13.5413C8.96462 13.5413 8.12515 12.7019 8.12515 11.6663C8.12515 10.6308 8.96462 9.79134 10.0002 9.79134ZM13.1252 11.6663C13.1252 9.94045 11.726 8.54134 10.0002 8.54134C8.27426 8.54134 6.87515 9.94045 6.87515 11.6663C6.87515 13.3922 8.27426 14.7913 10.0002 14.7913C11.726 14.7913 13.1252 13.3922 13.1252 11.6663Z"
      fill={ACTION_TOOLBAR_ICON_FILL}
    />
  </svg>
);

/** Voice input (Figma SVG, mic — 20×20 in 32×32 control). */
// const IconVoice = (): JSX.Element => (
//   <svg
//     width={20}
//     height={20}
//     viewBox="0 0 20 20"
//     fill="none"
//     xmlns="http://www.w3.org/2000/svg"
//     aria-hidden="true"
//     focusable="false"
//     className="pointer-events-none block shrink-0 select-none"
//   >
//     <path
//       d="M15.8334 7.70866C16.1786 7.70866 16.4584 7.98848 16.4584 8.33366V9.16699C16.4584 12.523 13.8986 15.28 10.6251 15.5944V17.7087H12.5001C12.8453 17.7087 13.1251 17.9885 13.1251 18.3337C13.1251 18.6788 12.8453 18.9587 12.5001 18.9587H7.50008C7.1549 18.9587 6.87508 18.6788 6.87508 18.3337C6.87508 17.9885 7.1549 17.7087 7.50008 17.7087H9.37508V15.5944C6.10155 15.28 3.54175 12.523 3.54175 9.16699V8.33366C3.54175 7.98848 3.82157 7.70866 4.16675 7.70866C4.51193 7.70866 4.79175 7.98848 4.79175 8.33366V9.16699C4.79175 12.0435 7.1236 14.3753 10.0001 14.3753C12.8766 14.3753 15.2084 12.0435 15.2084 9.16699V8.33366C15.2084 7.98848 15.4882 7.70866 15.8334 7.70866Z"
//       fill={ACTION_TOOLBAR_ICON_FILL}
//     />
//     <path
//       fillRule="evenodd"
//       clipRule="evenodd"
//       d="M10.0001 1.04199C11.726 1.04199 13.1251 2.4411 13.1251 4.16699V9.16699C13.1251 10.8929 11.726 12.292 10.0001 12.292C8.27419 12.292 6.87508 10.8929 6.87508 9.16699V4.16699C6.87508 2.4411 8.27419 1.04199 10.0001 1.04199ZM10.0001 2.29199C8.96455 2.29199 8.12508 3.13146 8.12508 4.16699V9.16699C8.12508 10.2025 8.96455 11.042 10.0001 11.042C11.0356 11.042 11.8751 10.2025 11.8751 9.16699V4.16699C11.8751 3.13146 11.0356 2.29199 10.0001 2.29199Z"
//       fill={ACTION_TOOLBAR_ICON_FILL}
//     />
//   </svg>
// );

/** Map pin only (no English wordmark — place name is shown beside the icon). */
const IconLocation = (): JSX.Element => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
    <path
      d="M12.001 8.10938C12.9675 8.10938 13.751 8.89288 13.751 9.85938C13.751 10.8259 12.9675 11.6094 12.001 11.6094C11.0345 11.6094 10.251 10.8259 10.251 9.85938C10.251 8.89288 11.0345 8.10938 12.001 8.10938Z"
      fill={ICON_FILL}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.001 1.10938C16.8335 1.10938 20.751 5.02688 20.751 9.85938C20.751 11.1611 20.1722 12.6018 19.4043 13.9658C18.625 15.35 17.5962 16.75 16.582 17.9912C15.5656 19.2352 14.5513 20.3365 13.792 21.126C13.4121 21.521 13.0948 21.8387 12.8721 22.0586C12.7608 22.1684 12.6727 22.2541 12.6123 22.3125L12.542 22.3799L12.5244 22.3975L12.5186 22.4014L12.5176 22.4033L12.001 22.8926L11.4844 22.4033L11.4775 22.3975L11.46 22.3799L11.3896 22.3125C11.3292 22.2541 11.2412 22.1684 11.1299 22.0586C10.9071 21.8387 10.5899 21.521 10.21 21.126C9.45069 20.3365 8.43635 19.2352 7.41992 17.9912C6.40577 16.75 5.37699 15.35 4.59766 13.9658C3.82972 12.6018 3.25098 11.1611 3.25098 9.85938C3.25098 5.02688 7.16848 1.10938 12.001 1.10938ZM12.001 2.60938C7.99691 2.60938 4.75098 5.85531 4.75098 9.85938C4.75098 10.7667 5.17232 11.9304 5.9043 13.2305C6.62496 14.5105 7.59618 15.8355 8.58203 17.042C9.56548 18.2456 10.5514 19.3159 11.292 20.0859C11.5669 20.3718 11.8074 20.6161 12.001 20.8096C12.1946 20.6161 12.435 20.3718 12.71 20.0859C13.4506 19.3159 14.4365 18.2456 15.4199 17.042C16.4058 15.8355 17.377 14.5105 18.0977 13.2305C18.8296 11.9304 19.251 10.7667 19.251 9.85938C19.251 5.85531 16.005 2.60938 12.001 2.60938Z"
      fill={ICON_FILL}
    />
  </svg>
);

export type HeaderDGASecondNavBarProps = {
  /** Shown when geolocation is denied or unavailable (sanitized when set) */
  fallbackLocationLabel?: string;
  /** Site / route locale (e.g. from Sitecore JSS or Next.js); drives translations and `Intl` formatting */
  locale?: string;
};

const HeaderDGASecondNavBar = ({
  fallbackLocationLabel = "",
  locale: localeProp,
}: HeaderDGASecondNavBarProps): JSX.Element => {
  const [now, setNow] = useState(() => new Date());
  const [resolvedLocale, setResolvedLocale] = useState(() =>
    sanitizeLocale(
      localeProp ?? (typeof navigator !== "undefined" ? navigator.language : "en-GB")
    )
  );
  const [zoomPct, setZoomPct] = useState(DEFAULT_ZOOM_PCT);
  const [isGrayscale, setIsGrayscale] = useState(false);

  useEffect(() => {
    const fromBrowser = typeof navigator !== "undefined" ? navigator.language : "en-GB";
    setResolvedLocale(sanitizeLocale(localeProp ?? fromBrowser));
  }, [localeProp]);

  const { weatherCode, locationLabel, showGeoRows, geoRowsLoading } = useHeaderSecondNavGeo({
    resolvedLocale,
    fallbackLocationLabel,
  });

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const z = readStoredZoom();
    setZoomPct(z);
    applyRootFontZoom(z);
  }, []);

  useEffect(() => {
    setIsGrayscale(getGrayscaleModeEnabled());
    const onChange = () => setIsGrayscale(getGrayscaleModeEnabled());
    window.addEventListener(DGA_GRAYSCALE_CHANGE_EVENT, onChange);
    return () => window.removeEventListener(DGA_GRAYSCALE_CHANGE_EVENT, onChange);
  }, []);

  const messages = getSecondNavMessages(resolvedLocale);
  const dateLabel = formatNavDate(now, resolvedLocale);
  const timeLabel = formatNavTime(now, resolvedLocale);
  const weatherDisplay =
    weatherCode !== null
      ? sanitizeDisplayText(weatherCodeToLabel(weatherCode, resolvedLocale), 64)
      : messages.unavailable;

  const locationSanitized = sanitizeDisplayText(locationLabel, 120);
  const locationDisplay =
    locationSanitized.length > 0 && locationSanitized !== "—" ? locationSanitized : messages.unavailable;

  const zoomOut = useCallback(() => {
    setZoomPct((z) => applyRootFontZoom(z - ZOOM_STEP_PCT));
  }, []);

  const zoomIn = useCallback(() => {
    setZoomPct((z) => applyRootFontZoom(z + ZOOM_STEP_PCT));
  }, []);

  const toggleContrast = useCallback(() => {
    setIsGrayscale(toggleGrayscaleMode());
  }, []);

  return (
    <section
      className="border-t border-b border-neutral-300 bg-neutral-100"
      aria-label={messages.a11y.section}
      aria-busy={geoRowsLoading}
    >
      <div className="responsive flex h-auto min-h-10 flex-wrap items-center justify-between gap-x-4 gap-y-2 py-2 md:h-10 md:py-0">
        <ul
          className="m-0 flex min-w-0 list-none flex-wrap items-center gap-4 p-0"
          aria-label={messages.a11y.contextList}
        >
          {showGeoRows && (
            <SecondNavItem
              icon={<IconCloud />}
              visibleLabel={weatherDisplay}
              groupAriaLabel={
                geoRowsLoading
                  ? messages.a11y.loadingWeather
                  : `${messages.a11y.weather}, ${weatherDisplay}`
              }
              isLoading={geoRowsLoading}
            />
          )}
          <SecondNavItem
            icon={<IconCalendar />}
            visibleLabel={dateLabel}
            groupAriaLabel={`${messages.a11y.date}, ${dateLabel}`}
          />
          <SecondNavItem
            icon={<IconClock />}
            visibleLabel={timeLabel}
            groupAriaLabel={`${messages.a11y.time}, ${timeLabel}`}
          />
          {showGeoRows && (
            <SecondNavItem
              icon={<IconLocation />}
              visibleLabel={locationDisplay}
              groupAriaLabel={
                geoRowsLoading
                  ? messages.a11y.loadingLocation
                  : `${messages.a11y.location}, ${locationDisplay}`
              }
              isLoading={geoRowsLoading}
            />
          )}
        </ul>
        <div
          className="flex items-center gap-1.5"
          role="toolbar"
          aria-label={messages.a11y.toolbar}
          aria-orientation="horizontal"
        >
          {/* <button
            type="button"
            className={ACTION_BTN_CLASS}
            disabled
            aria-label={messages.a11y.voiceInput}
            title={messages.a11y.voiceInput}
          >
            <IconVoice />
          </button> */}
          <button
            type="button"
            className={ACTION_BTN_CLASS}
            aria-label={`${messages.a11y.zoomOut}. ${messages.zoomWithPercent(zoomPct)}`}
            onClick={zoomOut}
            disabled={zoomPct <= MIN_ZOOM_PCT}
          >
            <IconZoomOut />
          </button>
          <button
            type="button"
            className={ACTION_BTN_CLASS}
            aria-label={`${messages.a11y.zoomIn}. ${messages.zoomWithPercent(zoomPct)}`}
            onClick={zoomIn}
            disabled={zoomPct >= MAX_ZOOM_PCT}
          >
            <IconZoomIn />
          </button>
          <button
            type="button"
            className={ACTION_BTN_CLASS}
            aria-label={
              isGrayscale
                ? `${messages.a11y.contrastOn}. ${messages.a11y.viewContrast}`
                : `${messages.a11y.contrastOff}. ${messages.a11y.viewContrast}`
            }
            aria-pressed={isGrayscale}
            onClick={toggleContrast}
          >
            <IconContrastView />
          </button>
        </div>
      </div>
      </section>
  );
};

export default HeaderDGASecondNavBar;
